import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const DEFAULT_TONE = 'ding';
const DEFAULT_ALERT_TYPE = ['none'];
// An inbound message and the auto-routing that follows it -- or a repeated
// 'assignee.changed' broadcast for the same assignment -- arrive as separate
// events. Within this window, per conversation, they are treated as one
// alert-worthy moment. Deferred routing (a pool job picking up a ticket once
// an agent frees up) can legitimately take several seconds, hence the size.
const ALERT_DEDUPE_WINDOW = 5000;
// The OTHER direction -- an assignment alert followed by that same message's
// own broadcast -- is different: synchronous auto-assignment sets the
// assignee before the message row even exists, so the two arrive within
// milliseconds of each other, never seconds. Reusing ALERT_DEDUPE_WINDOW here
// would also swallow a genuinely separate second message sent shortly after
// being routed (e.g. a quick chat follow-up), which deserves its own alert.
const ASSIGNMENT_MESSAGE_CORRELATION_WINDOW = 2000;

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
    };

    this.recurringNotificationTimer = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
    };

    this.currentUser = null;

    this.lastRoutableAlertAtByConversation = new Map();
    this.lastAssignmentAlertAtByConversation = new Map();
  }

  intializeAudio = () => {
    const resourceUrl = `${ALERT_PATH_PREFIX}${this.audioConfig.tone}.mp3`;
    this.audioConfig.audio = new Audio(resourceUrl);
    return this.audioConfig.audio.load();
  };

  playAudioAlert = async () => {
    try {
      await this.audioConfig.audio.play();
    } catch (error) {
      if (
        error.name === 'NotAllowedError' &&
        !this.hasSentSoundPermissionsRequest
      ) {
        this.hasSentSoundPermissionsRequest = true;
        useAlert(
          'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
          { usei18n: true, duration: ALERT_DURATION }
        );
      }
    }
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    audioAlertType = DEFAULT_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
  }) => {
    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: audioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
    };

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: audioAlertTone,
    };

    // Also initialise when no element exists yet. An agent whose tone resolves
    // to the default matches previousAudioTone on the very first set(), so a
    // tone-changed check alone leaves audio null forever -- and playAudioAlert
    // then fails silently on null.play().
    if (previousAudioTone !== audioAlertTone || !this.audioConfig.audio) {
      this.intializeAudio();
    }

    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  executeRecurringNotification = () => {
    if (this.store.hasUnreadConversation() && this.shouldPlayAlert()) {
      this.playAudioAlert();
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;

    //  Audio alert is disabled dismiss the timer
    if (audioAlertType.includes('none')) return;

    // If unread conversation flag is disabled, dismiss the timer
    if (!alertIfUnreadConversationExist) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;
    if (audioAlertType.includes('all')) return true;

    const assignedToMe = isConversationAssignedToMe(
      message,
      this.currentUser.id
    );
    const isUnassigned = isConversationUnassigned(message);

    const shouldPlayAudio = [];

    if (
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    ) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  shouldNotifyOnAssignment = () => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;

    // Being handed a ticket is an event about a conversation of mine, so it is
    // the "assigned" preference that governs it. 'unassigned' and 'notme' say
    // nothing about tickets landing on my own plate.
    return (
      audioAlertType.includes('all') ||
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    );
  };

  // OK to play unless the agent is at the dashboard already reading this
  // conversation, or only wants alerts while the window is in the background.
  shouldPlayByBackgroundState = conversationId => {
    if (!WindowVisibilityHelper.isWindowVisible()) return true;
    if (this.store.isCurrentConversation(conversationId)) return false;

    return this.shouldPlayAlert();
  };

  // "Routable" alerts are the ones that plausibly concern the SAME ticket
  // landing on my plate: a real assignment-to-me, or a message on a
  // conversation that was unassigned or already mine at the time. A message
  // alert that fired only because the ticket belonged to someone else
  // ('notme') or because 'all' matches every message regardless of assignee
  // is not routable -- it says nothing about whether *this* conversation is
  // about to become mine, so it must not be able to swallow a later, genuine
  // assignment.
  wasRecentlyAlertedForRouting = conversationId => {
    const alertedAt =
      this.lastRoutableAlertAtByConversation.get(conversationId);
    return (
      alertedAt !== undefined && Date.now() - alertedAt < ALERT_DEDUPE_WINDOW
    );
  };

  // Narrower than wasRecentlyAlertedForRouting in two ways: true only when
  // the *assignment* path caused the recent alert, and using the much
  // shorter ASSIGNMENT_MESSAGE_CORRELATION_WINDOW (see its comment) rather
  // than ALERT_DEDUPE_WINDOW -- so a genuinely separate message sent shortly
  // after being routed still gets its own alert, instead of being silenced by
  // either an earlier message's alert or a same-conversation assignment from
  // seconds ago that this message has nothing to do with.
  wasRecentlyAlertedForAssignment = conversationId => {
    const alertedAt =
      this.lastAssignmentAlertAtByConversation.get(conversationId);
    return (
      alertedAt !== undefined &&
      Date.now() - alertedAt < ASSIGNMENT_MESSAGE_CORRELATION_WINDOW
    );
  };

  // Prevents the maps from growing unbounded over a long-running dashboard
  // session -- entries outside the dedupe window can never affect
  // wasRecentlyAlertedForRouting again, so they're safe to drop.
  pruneExpiredAlerts = () => {
    const now = Date.now();
    const maps = [
      this.lastRoutableAlertAtByConversation,
      this.lastAssignmentAlertAtByConversation,
    ];
    maps.forEach(map => {
      map.forEach((at, conversationId) => {
        if (now - at >= ALERT_DEDUPE_WINDOW) {
          map.delete(conversationId);
        }
      });
    });
  };

  // isRoutable defaults to isAssignment because an assignment alert is
  // definitionally about this conversation being mine -- callers only need
  // to pass it explicitly for message alerts, where it depends on who the
  // conversation was assigned to at the time (see wasRecentlyAlertedForRouting).
  triggerAlert = (
    conversationId,
    { isAssignment = false, isRoutable = isAssignment } = {}
  ) => {
    const now = Date.now();
    if (isRoutable) {
      this.lastRoutableAlertAtByConversation.set(conversationId, now);
    }
    if (isAssignment) {
      this.lastAssignmentAlertAtByConversation.set(conversationId, now);
    }
    this.pruneExpiredAlerts();
    this.playAudioAlert();
    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  // Auto-routing assigns a conversation in a separate write from the inbound
  // message, so it arrives as its own 'assignee.changed' event with no message
  // attached. Without this path a ticket routed to an agent after its first
  // message never makes a sound.
  onAssigneeChanged = payload => {
    const { id: conversationId, meta, status } = payload || {};
    const assigneeId = meta?.assignee?.id;

    if (!conversationId) return;

    // This event is broadcast to every member of the inbox, so only the agent
    // who actually received the ticket should hear it. Checked before
    // hasConversationPermission below: by the time that runs, currentUser is
    // guaranteed non-null (its id just matched assigneeId), so the permission
    // lookup can never see a null user and throw.
    if (!assigneeId || assigneeId !== this.currentUser?.id) return;

    if (!this.store.hasConversationPermission(this.currentUser)) return;

    if (status === wootConstants.STATUS_TYPE.PENDING) return;

    if (!this.shouldNotifyOnAssignment()) return;

    // Deliberately NOT compared against the assignee in the store. Routing also
    // emits 'conversation.updated' carrying the new assignee, and it arrives
    // first, so by this point the store already reports the new assignee for
    // every genuine assignment -- a store comparison here suppresses the alert
    // it is meant to allow. The dedupe window below is what stops a repeat
    // broadcast -- or the message that this assignment is auto-routing -- from
    // alerting twice, without also swallowing a genuinely new assignment that
    // merely happens to follow some unrelated alert on this conversation.
    if (this.wasRecentlyAlertedForRouting(conversationId)) return;

    if (!this.shouldPlayByBackgroundState(conversationId)) return;

    this.triggerAlert(conversationId, { isAssignment: true });
  };

  onNewMessage = message => {
    // If the user does not have the permission to view the conversation, then dismiss the alert
    // FIX ME: There shouldn't be a new message if the user has no access to the conversation.
    if (!this.store.hasConversationPermission(this.currentUser)) {
      return;
    }

    // If the conversation status is pending, then dismiss the alert
    // This case is common for all audio event types
    if (this.store.isMessageFromPendingConversation(message)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    if (!this.shouldNotifyOnMessage(message)) {
      return;
    }

    // If the message type is not incoming or private, then dismiss the alert
    const { message_type: messageType, private: isPrivate } = message;
    if (messageType !== MESSAGE_TYPE.INCOMING && !isPrivate) {
      return;
    }

    // If the user is looking at the conversation, or has disabled alerts while
    // active on the dashboard, then dismiss the alert
    if (!this.shouldPlayByBackgroundState(message.conversation_id)) {
      return;
    }

    // Routing can assign a conversation before its message broadcast arrives
    // here, in which case onAssigneeChanged already alerted for it. Checking
    // the assignment-only record (not the generic one) means a second,
    // distinct message still gets its own alert instead of being silenced by
    // the first message's own alert.
    if (this.wasRecentlyAlertedForAssignment(message.conversation_id)) {
      return;
    }

    // Only a message on a conversation that was unassigned or already mine
    // is "routable" -- i.e. could plausibly be the same ticket an assignment
    // alert shortly after is auto-routing. A message alert that fired only
    // via 'notme' (someone else's ticket) or 'all' matching regardless of
    // assignee says nothing about that, and must not suppress a later,
    // genuine assignment of this conversation to me.
    const isRoutable =
      isConversationUnassigned(message) ||
      isConversationAssignedToMe(message, this.currentUser.id);
    this.triggerAlert(message.conversation_id, { isRoutable });
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
